import { Avatar, Col, Icon, Tappable, Text, useTheme } from "@rally/ui-kit";

import { useState } from "react";
import { say } from "../../localization/say.ts";
import { drawnToneFor } from "../../me/a-drawn-face.ts";
import { calledBy } from "../../me/called.ts";
import { useFace } from "../../me/face.ts";
import { usePresent } from "../../push/here.ts";
import type { GoesTo } from "../composer/addressed.ts";
import type { Candidate } from "../composer/mentions.ts";
import { RoutePanel } from "./route-panel.tsx";

export const A_FACE_IS = 16;

export type Who = { who: Candidate; word: GoesTo["word"] };

export function RouteFace({ who, size = A_FACE_IS }: { who: Candidate; size?: number }) {
  const face = useFace(who.avatar);
  const present = usePresent(who.account);
  return (
    <Avatar
      name={calledBy(who) ?? who.handle}
      src={face}
      size={size}
      tone={who.kind === "agent" ? drawnToneFor(who.handle) : "neutral"}
      present={present}
    />
  );
}

function Chip({ one, phone, onDrop }: { one: Who; phone: boolean; onDrop: () => void }) {
  const theme = useTheme();
  const name = calledBy(one.who) ?? one.who.handle;
  const guess = one.word === "likely";
  const inside = (
    <>
      <RouteFace who={one.who} />
      {phone ? null : (
        <Text typeName="caption" color={guess ? "text.secondary" : "text.primary"} numberOfLines={1}>
          {name}
        </Text>
      )}
    </>
  );
  const skin = {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: theme.space[1],
    paddingLeft: 2,
    paddingRight: phone ? 2 : theme.space[2],
    paddingVertical: 2,
    borderRadius: 999,
    borderWidth: 1,
    borderStyle: guess ? ("dashed" as const) : ("solid" as const),
    borderColor: guess ? theme.color["border.default"] : theme.color.accent,
    backgroundColor: guess ? "transparent" : theme.color["bg.surfaceAlt"],
  };
  if (!guess) {
    return (
      <Col testID={`route-typed-${one.who.handle}`} style={skin}>
        {inside}
      </Col>
    );
  }
  return (
    <Tappable
      accessibilityRole="button"
      accessibilityLabel={say("route.drop")(name)}
      testID={`route-guess-${one.who.handle}`}
      onPress={onDrop}
      {...({ onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault() } as object)}
      style={skin}
    >
      {inside}
    </Tappable>
  );
}

export function RouteLine({
  said,
  names,
  phone,
  on,
  onDrop,
  onAdd,
  onAuto,
}: {
  said: readonly GoesTo[];

  names: readonly Candidate[];
  phone: boolean;
  on: boolean;
  onDrop: (handle: string) => void;
  onAdd: (handle: string) => void;
  onAuto: (on: boolean) => void;
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const byHandle = new Map(names.map((one) => [one.handle, one]));
  const rows: Who[] = said
    .map((who) => {
      const found = byHandle.get(who.handle);
      return found ? { who: found, word: who.word } : null;
    })
    .filter((one): one is Who => one !== null);
  const chips = rows.filter((one) => one.word !== "unlikely");

  if (chips.length === 0 && on) return null;

  return (
    <Col
      testID="route-line"
      style={{ position: "relative", flexDirection: "row", alignItems: "center", gap: theme.space[1] }}
    >
      {open ? (
        <RoutePanel
          rows={rows}
          on={on}
          onDrop={(handle) => onDrop(handle)}
          onAdd={(handle) => onAdd(handle)}
          onAuto={onAuto}
        />
      ) : null}
      <Icon name="arrow-right" size={12} color="text.tertiary" />
      {chips.map((one) => (
        <Chip key={one.who.account} one={one} phone={phone} onDrop={() => onDrop(one.who.handle)} />
      ))}
      <Tappable
        accessibilityRole="button"
        accessibilityLabel={say("route.who")}
        accessibilityState={{ expanded: open }}
        testID="route-open"
        onPress={() => setOpen((was) => !was)}
        {...({ onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault() } as object)}
        style={{
          padding: 2,
          borderRadius: theme.radius.sm,
        }}
      >
        <Icon name={open ? "chevron-up" : "chevron-down"} size={14} color="text.tertiary" />
      </Tappable>
    </Col>
  );
}
